import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { css } from "@/domains/emotion";
import { useAppStore } from "@/store";
import { GatedFeature } from "@/store/feature-gates/types";
import { MDSProgressBar, MDSProgressBarColor, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { useEffect, useState } from "react";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

type GateCheckResult = {
  allowed: boolean;
  used: number;
  limit: number;
  warnAt: number | null;
};

export const UsageProgress = observer(() => {
  const { store } = useAppStore();
  useEffect(() => {
    setChat(store.featureGates.checkGate(GatedFeature.ChatMessageSend));
    // Observe sessions and session events to subscribe and activate the live query
  }, [store.sessionEvents.size, store.sessions.size, store.featureGates]);

  const [notes, setNotes] = useState<GateCheckResult | null>(null);
  const [chat, setChat] = useState<GateCheckResult | null>(null);

  useEffectOnMount(() => {
    Promise.all([
      store.featureGates.checkGate(GatedFeature.NoteCreate),
      store.featureGates.checkGate(GatedFeature.ChatMessageSend),
    ])
      .then(([notesResult, chatResult]) => {
        setNotes(notesResult);
        setChat(chatResult);
      })
      .catch(error => {
        logger.error({
          message: "[SYNC][UsageProgress] Error checking feature gates",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      });
  });

  const formatValue = (usage: GateCheckResult | null) => {
    if (!usage) return "—";
    return `${usage.used} / ${usage.limit === Infinity ? "∞" : usage.limit}`;
  };

  return (
    <RowGrid>
      {/* Notes row */}
      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
        New notes
      </MDSText>
      <MDSProgressBar
        className={inlineBarStyles}
        value={notes?.used ?? 0}
        minValue={0}
        maxValue={notes ? (notes.limit === Infinity ? Math.max(1, notes.used) : notes.limit) : 25}
        aria-valuetext={formatValue(notes)}
        size="xlarge"
        color={
          notes
            ? notes.limit === Infinity || notes.allowed
              ? MDSProgressBarColor.Emphasis
              : MDSProgressBarColor.Danger
            : MDSProgressBarColor.Default
        }
        animation={{ duration: 0 }}
        rounded
      />
      <MDSText variant={MDSTextVariant.TextS} className={rightValueStyles}>
        {formatValue(notes)}
      </MDSText>

      {/* Chat row */}
      <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
        Chat messages
      </MDSText>
      <MDSProgressBar
        className={inlineBarStyles}
        value={chat ? chat.used : 0}
        minValue={0}
        maxValue={chat ? (chat.limit === Infinity ? Math.max(1, chat.used) : chat.limit) : 25}
        aria-valuetext={formatValue(chat)}
        size="xlarge"
        color={
          chat
            ? chat.limit === Infinity || chat.allowed
              ? MDSProgressBarColor.Emphasis
              : MDSProgressBarColor.Danger
            : MDSProgressBarColor.Default
        }
        animation={{ duration: 0 }}
        rounded
      />
      <MDSText variant={MDSTextVariant.TextS} className={rightValueStyles}>
        {formatValue(chat)}
      </MDSText>
    </RowGrid>
  );
});

const RowGrid = styled.div(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "max-content 1fr max-content",
  alignItems: "center",
  columnGap: 16,
  rowGap: theme.spacing[5],
  margin: `${theme.spacing[5]} 0`,
  width: "100%",
}));

// Override the internal grid of MDSProgressBar to render only the bar inline
const inlineBarStyles = css({
  display: "grid",
  gridTemplateAreas: '"bar"',
  gridTemplateColumns: "1fr",
  gap: 0,
  width: "100%",
  // Disable any animations/transitions inside the MDSProgressBar
  ".progress-fill": {
    transition: "none !important",
    animation: "none !important",
  },
});

const rightValueStyles = css({ display: "flex", alignItems: "center", justifyContent: "flex-end" });
